import Dexie, { type Table } from "dexie";

/**
 * The bridge marker lives in its own database so a newer upload schema cannot make the marker
 * unreadable to an older bridge build. It is intentionally small and contains no upload bytes.
 */
const ADMIN_UPLOAD_MIGRATION_FENCE_DATABASE = "AdminUploadMigrationFence";
export const ADMIN_UPLOAD_MIGRATION_FENCE_KEY = "admin";
const ADMIN_UPLOAD_BRIDGE_RELEASE = 1;
const ADMIN_UPLOAD_TARGET_SCHEMA_VERSION = 1;
const ADMIN_UPLOAD_ADAPTER_CONTRACT_VERSION = 1;

export type AdminMigrationFenceState = "bridge-ready" | "adopted";

export interface AdminMigrationFence {
	key: typeof ADMIN_UPLOAD_MIGRATION_FENCE_KEY;
	bridgeRelease: number;
	targetSchemaVersion: number;
	adapterContractVersion: number;
	state: AdminMigrationFenceState;
	updatedAt: number;
}

export interface AdminUploadMigrationFenceStore {
	get(): Promise<AdminMigrationFence | undefined>;
	put(fence: AdminMigrationFence): Promise<void>;
}

export interface AdminUploadAdoptionFence {
	state: "adopted";
	engineFamily?: "legacy";
	schemaVersion?: number;
	adapterContractVersion?: number;
}

export interface AdminUploadMigrationWriterLease {
	ownerId: string;
	epoch: number;
	release(): Promise<void>;
}

export interface AdminUploadMigrationSource<Session, Chunk> {
	listSessions(): Promise<Session[]>;
	getSession(sessionId: string): Promise<Session | undefined>;
	getChunks(session: Session): Promise<Chunk[]>;
	/** Returns the durable writer-lease state, not a best-effort in-memory flag. */
	getLeaseState(session: Session): Promise<"free" | "live" | "indeterminate">;
	/** Holds the durable writer lease through verification and adoption. */
	acquireWriterLease?(session: Session): Promise<AdminUploadMigrationWriterLease | undefined>;
	/** Must be a source-row CAS. False means another writer changed the row. */
	markAdopted(
		sessionId: string,
		lease?: Pick<AdminUploadMigrationWriterLease, "ownerId" | "epoch">
	): Promise<AdminUploadAdoptionFence | false>;
	/**
	 * Final source-row CAS for a copy-out migration. Admin's bridge adopts IN PLACE — the source rows
	 * are the adapter's only durable bytes — so nothing calls this today. It stays on the contract
	 * because the legacy source still implements it; PR2 removes both together.
	 */
	deleteSession(
		sessionId: string,
		expectedFence: AdminUploadAdoptionFence,
		lease?: Pick<AdminUploadMigrationWriterLease, "ownerId" | "epoch">
	): Promise<boolean>;
}

export class AdminUploadMigrationBlockedError extends Error {
	readonly code = "ADMIN_UPLOAD_MIGRATION_BLOCKED";

	constructor(message: string) {
		super(message);
		this.name = "AdminUploadMigrationBlockedError";
	}
}

export class AdminUploadUpgradeRequiredError extends Error {
	readonly code = "ADMIN_UPLOAD_UPGRADE_REQUIRED";

	constructor(message: string) {
		super(message);
		this.name = "AdminUploadUpgradeRequiredError";
	}
}

class AdminUploadMigrationFenceDatabase extends Dexie {
	fences!: Table<AdminMigrationFence, string>;

	constructor() {
		super(ADMIN_UPLOAD_MIGRATION_FENCE_DATABASE);
		this.version(1).stores({ fences: "key, updatedAt" });
		// A bridge must never keep a stale connection open while a new build is trying to upgrade.
		this.on("versionchange", () => this.close());
		this.on("blocked", () => {
			throw new AdminUploadMigrationBlockedError("Upload migration is blocked by another tab; refresh after it closes.");
		});
	}
}

function createAdminUploadMigrationFenceStore(database: Pick<AdminUploadMigrationFenceDatabase, "fences">): AdminUploadMigrationFenceStore {
	return {
		get: () => database.fences.get(ADMIN_UPLOAD_MIGRATION_FENCE_KEY),
		put: (fence) => database.fences.put(fence).then(() => undefined)
	};
}

export function createDefaultAdminUploadMigrationFenceStore(): AdminUploadMigrationFenceStore {
	return createAdminUploadMigrationFenceStore(new AdminUploadMigrationFenceDatabase());
}

export interface AdminUploadMigrationFenceGateOptions {
	fence: AdminUploadMigrationFenceStore;
	supportedBridgeRelease?: number;
	supportedTargetSchemaVersion?: number;
	adapterContractVersion?: number;
	now?: () => number;
}

function isMigrationVersion(value: unknown): value is number {
	return typeof value === "number" && Number.isSafeInteger(value) && value >= 1;
}

function validateFence(
	fence: AdminMigrationFence,
	supportedBridgeRelease: number,
	supportedTargetSchemaVersion: number,
	supportedAdapterContractVersion: number
): void {
	// The fence is a durable cross-build admission boundary. Treat a corrupt timestamp as
	// unreadable rather than allowing a reopened bridge to proceed with NaN comparisons or
	// persist another poisoned marker.
	if (typeof fence.updatedAt !== "number" || !Number.isFinite(fence.updatedAt)) {
		throw new AdminUploadMigrationBlockedError("Upload migration fence has an invalid timestamp.");
	}
	if (
		!isMigrationVersion(fence.bridgeRelease) ||
		!isMigrationVersion(fence.targetSchemaVersion) ||
		!isMigrationVersion(fence.adapterContractVersion)
	) {
		throw new AdminUploadMigrationBlockedError("Upload migration fence has invalid version fields.");
	}
	if (
		!isMigrationVersion(supportedBridgeRelease) ||
		!isMigrationVersion(supportedTargetSchemaVersion) ||
		!isMigrationVersion(supportedAdapterContractVersion)
	) {
		throw new AdminUploadMigrationBlockedError("Upload migration support versions are invalid.");
	}
	if (
		fence.bridgeRelease > supportedBridgeRelease ||
		fence.targetSchemaVersion > supportedTargetSchemaVersion ||
		fence.adapterContractVersion > supportedAdapterContractVersion
	) {
		throw new AdminUploadUpgradeRequiredError(
			"This browser has a newer upload engine. Please refresh or update Contact before continuing the upload."
		);
	}
	if (fence.state !== "bridge-ready" && fence.state !== "adopted") {
		throw new AdminUploadMigrationBlockedError("Upload migration fence is not in a recognized state.");
	}
}

/**
 * The cross-build admission fence, with no opinion about what adoption does to a row. Admin's
 * adoption is an in-place stamp and lives in adminUploadMigrationIntegration; this gate owns only
 * publishing the marker, refusing a build that cannot understand it, and recording that a pass ran.
 */
export function createAdminUploadMigrationFenceGate(options: AdminUploadMigrationFenceGateOptions) {
	const supportedBridgeRelease = options.supportedBridgeRelease ?? ADMIN_UPLOAD_BRIDGE_RELEASE;
	const supportedTargetSchemaVersion = options.supportedTargetSchemaVersion ?? ADMIN_UPLOAD_TARGET_SCHEMA_VERSION;
	const adapterContractVersion = options.adapterContractVersion ?? ADMIN_UPLOAD_ADAPTER_CONTRACT_VERSION;
	const now = options.now ?? Date.now;
	const migrationTimestamp = (): number => {
		const value = now();
		if (!Number.isFinite(value)) throw new AdminUploadMigrationBlockedError("Upload migration fence has an invalid timestamp.");
		return value;
	};

	const readFence = async (): Promise<AdminMigrationFence> => {
		const fence = await options.fence.get();
		if (!fence) throw new AdminUploadMigrationBlockedError("Upload migration bridge has not been published yet.");
		validateFence(fence, supportedBridgeRelease, supportedTargetSchemaVersion, adapterContractVersion);
		return fence;
	};

	return {
		async prepareBridge(): Promise<AdminMigrationFence> {
			if (
				!isMigrationVersion(supportedBridgeRelease) ||
				!isMigrationVersion(supportedTargetSchemaVersion) ||
				!isMigrationVersion(adapterContractVersion)
			) {
				throw new AdminUploadMigrationBlockedError("Upload migration support versions are invalid.");
			}
			const existing = await options.fence.get();
			if (existing) {
				validateFence(existing, supportedBridgeRelease, supportedTargetSchemaVersion, adapterContractVersion);
				return existing;
			}
			const fence: AdminMigrationFence = {
				key: ADMIN_UPLOAD_MIGRATION_FENCE_KEY,
				bridgeRelease: supportedBridgeRelease,
				targetSchemaVersion: supportedTargetSchemaVersion,
				adapterContractVersion,
				state: "bridge-ready",
				updatedAt: migrationTimestamp()
			};
			await options.fence.put(fence);
			// Read-after-write is deliberate: a successful put only proves the local request completed,
			// while the next open must observe the same durable marker.
			const confirmed = await options.fence.get();
			if (!confirmed || confirmed.updatedAt !== fence.updatedAt || confirmed.state !== fence.state) {
				throw new AdminUploadMigrationBlockedError("Upload migration bridge could not confirm its durable fence.");
			}
			return confirmed;
		},

		/** Admits an upload writer only once the separately-versioned bridge fence reads back valid. */
		readFence,

		/**
		 * Re-reads (and so re-validates) the fence after an adoption pass, and records that adoption
		 * happened. The reread is not optional: another build may have published a newer fence while
		 * the pass ran, and this build must not keep writing once that is true.
		 */
		async recordAdoption(anyRowAdopted: boolean): Promise<void> {
			const finalFence = await readFence();
			if (anyRowAdopted && finalFence.state !== "adopted") {
				await options.fence.put({ ...finalFence, state: "adopted", updatedAt: migrationTimestamp() });
			}
		}
	};
}
